import type { IncidentRow, TicketRow } from "./types";
import { CLOSED_STATUSES } from "./types";

// Everything here is plain functions over arrays, so the server tests and the browser share one implementation.

export const NONE_LABEL = "(none)";
const DAY_MS = 86_400_000;
const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const londonParts = new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/London", year: "numeric", month: "2-digit", day: "2-digit" });

// The calendar day an instant falls on in London, as YYYY-MM-DD.
export function londonDay(iso: string): string {
  const parts = londonParts.formatToParts(new Date(iso));
  const get = (type: string): string => parts.find((part) => part.type === type)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

const dayNumber = (day: string): number => Date.UTC(Number(day.slice(0, 4)), Number(day.slice(5, 7)) - 1, Number(day.slice(8, 10))) / DAY_MS;
// 1 January 1970 was a Thursday.
const isWeekend = (day: number): boolean => {
  const weekday = (day + 4) % 7;
  return weekday === 0 || weekday === 6;
};

function weekendDaysBetween(first: number, last: number): number {
  const length = last - first + 1;
  const weeks = Math.floor(length / 7);
  let weekend = weeks * 2;
  for (let day = first + weeks * 7; day <= last; day += 1) {
    if (isWeekend(day)) {
      weekend += 1;
    }
  }
  return weekend;
}

// Days from start to end without the weekend days that fall on or between them: the Sheet's
// (end - start) - ((INT(end) - INT(start) + 1) - NETWORKDAYS(start, end)). Never below zero, and undefined when end is not after start.
export function businessDays(startIso: string, endIso: string): number | undefined {
  const start = Date.parse(startIso);
  const end = Date.parse(endIso);
  if (Number.isNaN(start) || Number.isNaN(end) || end < start) {
    return undefined;
  }
  const weekend = weekendDaysBetween(dayNumber(londonDay(startIso)), dayNumber(londonDay(endIso)));
  return Math.max(0, (end - start) / DAY_MS - weekend);
}

export const isClosedStatus = (status: string | undefined): boolean => status !== undefined && CLOSED_STATUSES.includes(status);

export type CategoryKey = "businessUnit" | "severity" | "raiser" | "feature" | "company" | "team" | "engineer" | "status" | "ticketType" | "huddle" | "pricingPlan";

export interface ReportTicket extends TicketRow {
  // London calendar day and month it was raised, YYYY-MM-DD and YYYY-MM.
  raisedDay: string;
  raisedMonth: string;
  closed: boolean;
  // Business days from raised to closed. Present for any ticket with a close date, as in the Sheet.
  daysToClose?: number;
  // Business days from each lifecycle date to the close date.
  pickedUpToClose?: number;
  inProgressToClose?: number;
  waitingCustomerToClose?: number;
  waitingCsToClose?: number;
}

export type DurationKey = "daysToClose" | "pickedUpToClose" | "inProgressToClose" | "waitingCustomerToClose" | "waitingCsToClose";

export function enrich(rows: readonly TicketRow[]): ReportTicket[] {
  return rows.map((row) => {
    const raisedDay = londonDay(row.raisedAt);
    const since = (from: string | undefined): number | undefined => (from && row.closedAt ? businessDays(from, row.closedAt) : undefined);
    return {
      ...row,
      raisedDay,
      raisedMonth: raisedDay.slice(0, 7),
      closed: isClosedStatus(row.status),
      daysToClose: since(row.raisedAt),
      pickedUpToClose: since(row.pickedUpAt),
      inProgressToClose: since(row.inProgressAt),
      waitingCustomerToClose: since(row.waitingCustomerAt),
      waitingCsToClose: since(row.waitingCsAt),
    };
  });
}

export const labelOf = (row: ReportTicket, key: CategoryKey): string => {
  const value = row[key];
  return value === undefined || value === "" ? NONE_LABEL : value;
};

export const FILTER_KEYS = ["businessUnit", "severity", "raiser", "feature", "company", "team", "engineer", "status", "ticketType", "huddle"] as const satisfies readonly CategoryKey[];
export type FilterKey = (typeof FILTER_KEYS)[number];

export interface TicketFilter {
  // Inclusive, YYYY-MM-DD, by the day the ticket was raised.
  from?: string;
  to?: string;
  // An empty or missing list means "any".
  values: Partial<Record<FilterKey, string[]>>;
  // Only tickets raised despite a "no ticket" verdict, or whose type the person changed from the AI's.
  triage?: TriageFlag;
}

export const TRIAGE_FLAGS = ["overridden", "typeChanged"] as const;
export type TriageFlag = (typeof TRIAGE_FLAGS)[number];

// The person changed the type triage chose.
export const isTypeChange = (row: ReportTicket): boolean => row.ticketTypeCorrected === true && row.aiTicketType !== undefined && row.aiTicketType !== row.ticketType;

export function applyFilter(rows: readonly ReportTicket[], filter: TicketFilter): ReportTicket[] {
  const selected = FILTER_KEYS.flatMap((key) => {
    const values = filter.values[key];
    return values && values.length > 0 ? [[key, new Set(values)] as const] : [];
  });
  return rows.filter((row) => {
    if (filter.from && row.raisedDay < filter.from) {
      return false;
    }
    if (filter.to && row.raisedDay > filter.to) {
      return false;
    }
    if (filter.triage === "overridden" && row.overridden !== true) {
      return false;
    }
    if (filter.triage === "typeChanged" && !isTypeChange(row)) {
      return false;
    }
    return selected.every(([key, values]) => values.has(labelOf(row, key)));
  });
}

export const average = (values: readonly number[]): number | undefined =>
  values.length === 0 ? undefined : values.reduce((sum, value) => sum + value, 0) / values.length;

const defined = (values: readonly (number | undefined)[]): number[] => values.filter((value): value is number => value !== undefined);

export function averageOf(rows: readonly ReportTicket[], key: DurationKey): { average?: number; count: number } {
  const values = defined(rows.map((row) => row[key]));
  return { average: average(values), count: values.length };
}

export interface Headline {
  raised: number;
  closed: number;
  averageDaysToClose?: number;
}

export function headline(rows: readonly ReportTicket[]): Headline {
  return { raised: rows.length, closed: rows.filter((row) => row.closed).length, averageDaysToClose: averageOf(rows, "daysToClose").average };
}

export interface Count {
  label: string;
  count: number;
}

const byCountThenLabel = (a: Count, b: Count): number => b.count - a.count || a.label.localeCompare(b.label);

export function countValues(values: readonly string[]): Count[] {
  const counts = new Map<string, number>();
  for (const value of values) {
    counts.set(value, (counts.get(value) ?? 0) + 1);
  }
  return [...counts].map(([label, count]) => ({ label, count })).sort(byCountThenLabel);
}

// With `skipNone`, tickets with no value are left out, as the huddle chart does for tickets raised before it was asked.
export function countBy(rows: readonly ReportTicket[], key: CategoryKey, options: { skipNone?: boolean } = {}): Count[] {
  const labels = rows.map((row) => labelOf(row, key));
  return countValues(options.skipNone ? labels.filter((label) => label !== NONE_LABEL) : labels);
}

// The biggest `limit` slices and one "Others" slice for the rest.
export function topWithOthers(counts: readonly Count[], limit: number): Count[] {
  if (counts.length <= limit) {
    return [...counts];
  }
  return [...counts.slice(0, limit), { label: "Others", count: counts.slice(limit).reduce((sum, item) => sum + item.count, 0) }];
}

export const monthLabel = (month: string): string => `${MONTH_NAMES[Number(month.slice(5, 7)) - 1]} ${month.slice(0, 4)}`;

// Every month from the first to the last in the data, so a quiet month shows as a gap and not as a skipped bar.
function monthsBetween(first: string, last: string): string[] {
  const months: string[] = [];
  let year = Number(first.slice(0, 4));
  let month = Number(first.slice(5, 7));
  const end = Number(last.slice(0, 4)) * 12 + Number(last.slice(5, 7));
  while (year * 12 + month <= end) {
    months.push(`${year}-${String(month).padStart(2, "0")}`);
    month += 1;
    if (month > 12) {
      month = 1;
      year += 1;
    }
  }
  return months;
}

const monthsOf = (rows: readonly ReportTicket[]): string[] => {
  const sorted = rows.map((row) => row.raisedMonth).sort();
  const first = sorted.at(0);
  const last = sorted.at(-1);
  return first === undefined || last === undefined ? [] : monthsBetween(first, last);
};

export interface MonthlyStack {
  months: { key: string; label: string }[];
  // Series names, biggest first.
  labels: string[];
  // counts[month][label index]
  counts: number[][];
}

// Tickets raised each month, split by a category.
export function monthlyStack(rows: readonly ReportTicket[], key: CategoryKey): MonthlyStack {
  const months = monthsOf(rows);
  const labels = countBy(rows, key).map((item) => item.label);
  const counts = months.map((month) => labels.map((label) => rows.filter((row) => row.raisedMonth === month && labelOf(row, key) === label).length));
  return { months: months.map((month) => ({ key: month, label: monthLabel(month) })), labels, counts };
}

// The average of a duration for tickets raised in each month (the Sheet's "average time to close by month").
export function monthlyAverage(rows: readonly ReportTicket[], key: DurationKey): { key: string; label: string; average?: number }[] {
  return monthsOf(rows).map((month) => ({
    key: month,
    label: monthLabel(month),
    average: average(defined(rows.filter((row) => row.raisedMonth === month).map((row) => row[key]))),
  }));
}

export function averageBy(rows: readonly ReportTicket[], key: CategoryKey, duration: DurationKey): { label: string; average?: number; count: number }[] {
  const groups = new Map<string, ReportTicket[]>();
  for (const row of rows) {
    const label = labelOf(row, key);
    groups.set(label, [...(groups.get(label) ?? []), row]);
  }
  return [...groups].map(([label, group]) => ({ label, ...averageOf(group, duration) }));
}

export interface CrossTab {
  columns: string[];
  rows: { label: string; cells: number[]; total: number }[];
  // Over every row of the data, not just the rows shown, so it matches the headline count.
  totals: number[];
  grandTotal: number;
}

// Counts of rows by two categories. `maxRows` keeps the biggest rows; the totals still cover everything.
export function crossTab(rows: readonly ReportTicket[], rowKey: CategoryKey, columnKey: CategoryKey, options: { maxRows?: number } = {}): CrossTab {
  const columns = countBy(rows, columnKey).map((item) => item.label);
  const grouped = new Map<string, number[]>();
  const totals = columns.map(() => 0);
  for (const row of rows) {
    const label = labelOf(row, rowKey);
    const column = columns.indexOf(labelOf(row, columnKey));
    const cells = grouped.get(label) ?? columns.map(() => 0);
    cells[column] = (cells[column] ?? 0) + 1;
    totals[column] = (totals[column] ?? 0) + 1;
    grouped.set(label, cells);
  }
  const all = [...grouped]
    .map(([label, cells]) => ({ label, cells, total: cells.reduce((sum, value) => sum + value, 0) }))
    .sort((a, b) => b.total - a.total || a.label.localeCompare(b.label));
  return { columns, rows: options.maxRows ? all.slice(0, options.maxRows) : all, totals, grandTotal: rows.length };
}

// One cross-tab per value of `groupKey` (for example a raiser and the features and statuses of their tickets).
export function groupedCrossTab(
  rows: readonly ReportTicket[],
  groupKey: CategoryKey,
  rowKey: CategoryKey,
  columnKey: CategoryKey,
  options: { maxGroups?: number; maxRowsPerGroup?: number } = {},
): { label: string; table: CrossTab }[] {
  const groups = countBy(rows, groupKey);
  return (options.maxGroups ? groups.slice(0, options.maxGroups) : groups).map(({ label }) => ({
    label,
    table: crossTab(
      rows.filter((row) => labelOf(row, groupKey) === label),
      rowKey,
      columnKey,
      { maxRows: options.maxRowsPerGroup },
    ),
  }));
}

export const percent = (part: number, whole: number): string => (whole === 0 ? "0%" : `${((part / whole) * 100).toFixed(1)}%`);

export interface TriageSummary {
  // Tickets raised in the app that triage gave an answer for. Imported Sheet tickets never went through triage.
  triaged: number;
  // Triage said no ticket was needed and the person raised one anyway.
  overrides: number;
  // Triage said no ticket was needed and the person closed the submission.
  acceptedNoTicket: number;
  // Triage said no ticket was needed and nobody acted, so the hourly sweep closed it.
  expiredNoTicket: number;
  // Triaged tickets where triage chose a type.
  classified: number;
  // Tickets whose type the person changed from the one triage chose.
  typeChanges: number;
  // For example "Bug → Customer Fix".
  typeChangesBy: Count[];
  byMonth: { key: string; label: string; overrides: number; acceptedNoTicket: number; typeChanges: number }[];
}

// How often people disagree with triage. `noTicket` is the submissions triage said needed no ticket that were closed without one.
export function summariseTriage(rows: readonly ReportTicket[], noTicket: readonly ReportTicket[] = []): TriageSummary {
  const triaged = rows.filter((row) => row.source === "app" && row.triageUnavailable !== true);
  const changed = triaged.filter(isTypeChange);
  const accepted = noTicket.filter((row) => row.closedBy === "person");
  return {
    triaged: triaged.length,
    overrides: triaged.filter((row) => row.overridden === true).length,
    acceptedNoTicket: accepted.length,
    expiredNoTicket: noTicket.filter((row) => row.closedBy === "system").length,
    classified: triaged.filter((row) => row.aiTicketType !== undefined).length,
    typeChanges: changed.length,
    typeChangesBy: countValues(changed.map((row) => `${row.aiTicketType} → ${row.ticketType ?? NONE_LABEL}`)),
    byMonth: monthsOf([...triaged, ...accepted]).map((month) => {
      const inMonth = triaged.filter((row) => row.raisedMonth === month);
      return {
        key: month,
        label: monthLabel(month),
        overrides: inMonth.filter((row) => row.overridden === true).length,
        acceptedNoTicket: accepted.filter((row) => row.raisedMonth === month).length,
        typeChanges: inMonth.filter(isTypeChange).length,
      };
    }),
  };
}

export interface IncidentSummary {
  total: number;
  bySeverity: Count[];
  byType: Count[];
  byMonth: { key: string; label: string; count: number }[];
}

export function summariseIncidents(incidents: readonly IncidentRow[]): IncidentSummary {
  const months = incidents.map((incident) => londonDay(incident.createdAt).slice(0, 7)).sort();
  const first = months.at(0);
  const last = months.at(-1);
  const span = first === undefined || last === undefined ? [] : monthsBetween(first, last);
  return {
    total: incidents.length,
    bySeverity: countValues(incidents.map((incident) => incident.severity)).sort((a, b) => a.label.localeCompare(b.label)),
    byType: countValues(incidents.flatMap((incident) => incident.types)),
    byMonth: span.map((key) => ({ key, label: monthLabel(key), count: months.filter((month) => month === key).length })),
  };
}
